import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DmcPackageService } from 'src/app/business-service/dmc-package/dmc-package.service';
import { InventoryService } from 'src/app/business-service/inventory/inventory.service';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ModalsService } from 'src/app/modules/shared/services/modals/modals.service';
import { ComponentReloadService } from 'src/app/modules/shared/services/reload/component-reload.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';

@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
})
export class NotesComponent  implements OnInit {

  @Input() leadId: any;
  @Input() clientId:any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeModel: EventEmitter<any> = new EventEmitter();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();
  @Input() itineraryDetails: any;

  @Output() updateQuote: EventEmitter<void> = new EventEmitter();
  @Output() updateInventoryItinerary: EventEmitter<void> = new EventEmitter();

  @Output() openInventoryItinerary: EventEmitter<void> = new EventEmitter

  @Input() storedFormData: any;
  @Input() editInventory: boolean = false;
  @Input() editQuote: boolean = false;

  @Input() dmcPackageId: any;
  dmcPackageNotes!: string;
  dmcNotesCopied: boolean = false;
  @Input() dmcOrganizationId: any;
  
  constructor(
    private _fb: FormBuilder,
    private _itineraryService: ItineraryService,
    private _inventoryService: InventoryService,
    private _globalService: GlobalService,
    private _responseNotificationService: ResponseNotificationService,
    private _modalPopupService: ModalsService,
    private _dmcPackageService: DmcPackageService
  ) { }

  notesForm!: FormGroup;

  async ngOnInit() {
    if(!this.dmcOrganizationId){
      this.dmcOrganizationId = this.itineraryDetails?.recommendedOrganizationId;
    }

    this.notesForm = this._fb.group({
      notes: [""]
    })

    this.notesForm.valueChanges.subscribe(()=>{
      this.notesForm.markAsTouched();
    })

    if(this.storedFormData){
      this.notesForm.patchValue({
        notes: this.storedFormData.note
      })
    }

    if(this.dmcPackageId){
      this.dmcPackageNotes = await this.getDmcPackageNotes(this.dmcPackageId);
    }    
  }

  async getDmcPackageNotes(packageId:string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this._dmcPackageService.getPackage(packageId, this.dmcOrganizationId).subscribe({
        next: (data) => {
          resolve(data.notes?.note);
        },
        error: (error) => {
          console.error('Error fetching package notes:', error);
          reject(error);
        }
      })
    })
  }

  async onSubmit(): Promise<boolean> {
    if (!this.notesForm.valid) {
      this.notesForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
      return false; // Form is invalid
    }
  
    try {
      const formData = await this.serializeFormData(this.notesForm.value);
  
      if (this.leadId) {
        // Handle lead-specific logic
        if (this.editQuote) {
          this.notesForm.markAsPristine();
          formData.dataSaved = true;
          this.storeFormData.emit(formData);
          this.updateQuote.emit();
        } else {
          await this._itineraryService.addnotes(formData).toPromise();
          this._responseNotificationService.showMessage("Notes Added!");
          this.notesForm.markAsPristine();
          formData.dataSaved = true;
          this.storeFormData.emit(formData);
        }
      } else {
        // Handle inventory-specific logic
        if (this.editInventory) {
          this.notesForm.markAsPristine();
          formData.dataSaved = true;
          this.storeFormData.emit(formData);
          this.updateInventoryItinerary.emit();
        } else {
          await this._inventoryService.addInventoryNotes(formData).toPromise();
          this._responseNotificationService.showMessage("Notes Added!");
          this.notesForm.markAsPristine();
          this.closeModel.emit(null); // Close model popup
          formData.dataSaved = true;
          this.storeFormData.emit(formData);
        }
      }
  
      return true; // Form submitted successfully
    } catch (error) {
      console.error("Error while submitting notes:", error);
      return false; // Submission failed
    }
  }
  

  private async serializeFormData(formData: any){
    const serializedData = {
      leadId: this.leadId,
      itineraryId:  this.itineraryId,
      note: formData.notes,
      organizationId: this.storedFormData?.organizationId,
      id: this.storedFormData ? this.storedFormData.id : null,
      createdBy: this.storedFormData ? this.storedFormData.createdBy : null,
      ...(this.storedFormData && { createDate: this.storedFormData.createDate })
    }

    return serializedData;
  }

  // open inventoru itinerary view

  openInventoryItineraryView(itineraryId:any){
    this._modalPopupService.openInventoryItineraryView(itineraryId,this.leadId, this.clientId);
  }

  async resetForm(){
    if(await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")){
      this.ngOnInit();
    }    
  }

  goPrevious(){
    this.viewChange.emit(true);
  }

  goNext(){
    if(this.leadId){
      this.viewChange.emit(false);
    }else{
      this.openInventoryItinerary.emit();
    }
  }

  async saveAndNext(){
    if(await this.onSubmit()){
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.notesForm.dirty || this.notesForm.invalid) && this.notesForm.touched;
  }

  copyDmcNotesToClipboard() {
    if (!this.dmcPackageNotes) return;

    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = this.dmcPackageNotes;
    document.body.appendChild(textArea);

    // Select and copy the text
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);

    // Update button text
    this.dmcNotesCopied = true;

    // Reset after 2 seconds
    setTimeout(() => {
      this.dmcNotesCopied = false;
    }, 2000);
  }


}
